import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';

// Nova FAQ Knowledge Base
const NOVA_KNOWLEDGE_BASE = {
  introduction: {
    greeting: "Hi! I'm Nova, your AI assistant. I can help you understand how each tool works, answer questions about security, troubleshooting, and guide you through conversions.",
    name: "Nova",
    role: "AI support assistant for SMRTPDFTools"
  },
  
  tools: {
    "pdf to word": {
      description: "Converts PDF files into editable Word documents (.docx).",
      details: "Best for scanned PDFs, reports, resumes, books, etc. Output formatting may vary depending on how the PDF was originally created.",
      keywords: ["pdf", "word", "docx", "convert", "editable"]
    },
    "word to pdf": {
      description: "Converts Word documents into clean, print-ready PDFs.",
      details: "Maintains fonts, formatting, spacing, and layout.",
      keywords: ["word", "pdf", "convert", "document"]
    },
    "xfa to pdf": {
      description: "Converts unsupported/legacy XFA-based forms into standard PDF files that open on all devices.",
      details: "Useful when a PDF shows a blank page or error like 'Please open in Adobe Reader.'",
      keywords: ["xfa", "form", "legacy", "adobe", "blank"]
    },
    "image to pdf": {
      description: "Converts JPG, JPEG, or PNG images into a single, clean PDF file.",
      details: "Users can upload multiple images and reorder them before converting.",
      keywords: ["image", "jpg", "jpeg", "png", "photo", "picture"]
    },
    "merge pdf": {
      description: "Combines multiple PDF files into one single document.",
      details: "Upload multiple PDFs and arrange them in your preferred order.",
      keywords: ["merge", "combine", "join", "multiple"]
    },
    "split pdf": {
      description: "Extracts specific pages from a PDF file.",
      details: "Select which pages you want to extract and download them separately.",
      keywords: ["split", "extract", "pages", "separate"]
    },
    "compress pdf": {
      description: "Reduces PDF file size without significant quality loss.",
      details: "Great for email attachments or uploading to websites with size limits.",
      keywords: ["compress", "reduce", "size", "smaller"]
    },
    "pdf to jpg": {
      description: "Converts PDF pages into JPG/JPEG image files.",
      details: "Each page becomes a separate image file.",
      keywords: ["pdf", "jpg", "jpeg", "image", "convert"]
    }
  },
  
  security: {
    keywords: ["security", "secure", "privacy", "private", "safe", "data", "delete", "store"],
    responses: [
      "Your files remain private. We process everything securely over HTTPS and delete your files automatically after a short period.",
      "We never store documents permanently. Download links expire shortly for your safety.",
      "All transfers use secure HTTPS encryption.",
      "SMRTPDFTools never reads, reviews, or uses your content."
    ]
  },
  
  troubleshooting: {
    "conversion failed": {
      keywords: ["fail", "error", "not working", "broken", "crash"],
      response: "Please try again with a fresh upload—sometimes files get corrupted during transfer. Ensure the file is not password-protected. If the issue continues, try saving your PDF again and re-uploading."
    },
    "slow upload": {
      keywords: ["slow", "taking long", "stuck", "loading"],
      response: "Large files or slow internet connections can cause delays. Try again or use a smaller file."
    },
    "formatting issues": {
      keywords: ["format", "looks wrong", "different", "layout", "font"],
      response: "PDF-to-Word accuracy depends on the original file structure. Scanned or heavily designed PDFs may convert differently."
    },
    "download issues": {
      keywords: ["download", "can't open", "won't open", "blocked"],
      response: "Your browser may have blocked the download. Please enable downloads or try another browser."
    },
    "page not responding": {
      keywords: ["freeze", "not responding", "stuck page"],
      response: "Refresh the page—your tools remain safe and nothing will be lost."
    }
  },
  
  account: {
    keywords: ["signup", "sign up", "login", "account", "register", "promo", "code", "discount", "membership", "plan", "free", "premium"],
    responses: {
      signup: "You can create a free account in seconds to unlock daily conversions.",
      promo: "If you have a promo code, enter it on the signup page. Discounts apply automatically.",
      plans: "Free plan offers limited daily conversions. Premium plan provides unlimited conversions with no ads."
    }
  },
  
  seasonal: {
    keywords: ["snow", "snowflake", "holiday", "christmas", "popup", "animation"],
    response: "Yes, the holiday effects are temporary—they automatically turn off after Jan 2. Snowflakes appear during the holiday season, and the holiday pop-up shows only for first-time users."
  },
  
  upcoming: {
    keywords: ["new", "upcoming", "future", "planned", "coming soon"],
    response: "We have exciting features planned! More converters (Edit PDF, PDF OCR, Compress Enhancements), improved layout, and better mobile experience are coming soon. User feedback actively shapes new features."
  },
  
  limits: {
    keywords: ["limit", "max", "size", "maximum", "how big", "file size"],
    response: "The system supports most standard file sizes. If your file is extremely large or complex, try compressing it first or splitting it."
  }
};

// Find best response based on user input
const findResponse = (input) => {
  const lowerInput = input.toLowerCase();
  
  // Check for greetings
  if (/^(hi|hello|hey|howdy|greetings)/i.test(lowerInput)) {
    return NOVA_KNOWLEDGE_BASE.introduction.greeting;
  }
  
  // Check for tool-specific questions - prioritize exact tool name matches first
  // Order tools by specificity (more specific names first)
  const toolOrder = ['xfa to pdf', 'image to pdf', 'word to pdf', 'pdf to word', 'pdf to jpg', 'merge pdf', 'split pdf', 'compress pdf'];
  
  for (const toolName of toolOrder) {
    const toolInfo = NOVA_KNOWLEDGE_BASE.tools[toolName];
    if (toolInfo && lowerInput.includes(toolName.replace(/ /g, ''))) {
      return `**${toolName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}**: ${toolInfo.description} ${toolInfo.details}`;
    }
    if (toolInfo && lowerInput.includes(toolName)) {
      return `**${toolName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}**: ${toolInfo.description} ${toolInfo.details}`;
    }
  }
  
  // Then check by keywords
  for (const [toolName, toolInfo] of Object.entries(NOVA_KNOWLEDGE_BASE.tools)) {
    if (toolInfo.keywords.some(keyword => lowerInput.includes(keyword) && keyword.length > 3)) {
      return `**${toolName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}**: ${toolInfo.description} ${toolInfo.details}`;
    }
  }
  
  // Check for security questions
  if (NOVA_KNOWLEDGE_BASE.security.keywords.some(keyword => lowerInput.includes(keyword))) {
    const responses = NOVA_KNOWLEDGE_BASE.security.responses;
    return responses[Math.floor(Math.random() * responses.length)];
  }
  
  // Check for troubleshooting
  for (const [issue, data] of Object.entries(NOVA_KNOWLEDGE_BASE.troubleshooting)) {
    if (data.keywords.some(keyword => lowerInput.includes(keyword))) {
      return data.response;
    }
  }
  
  // Check for account questions
  if (NOVA_KNOWLEDGE_BASE.account.keywords.some(keyword => lowerInput.includes(keyword))) {
    if (lowerInput.includes("promo") || lowerInput.includes("code") || lowerInput.includes("discount")) {
      return NOVA_KNOWLEDGE_BASE.account.responses.promo;
    }
    if (lowerInput.includes("plan") || lowerInput.includes("premium") || lowerInput.includes("free")) {
      return NOVA_KNOWLEDGE_BASE.account.responses.plans;
    }
    return NOVA_KNOWLEDGE_BASE.account.responses.signup;
  }
  
  // Check for seasonal questions
  if (NOVA_KNOWLEDGE_BASE.seasonal.keywords.some(keyword => lowerInput.includes(keyword))) {
    return NOVA_KNOWLEDGE_BASE.seasonal.response;
  }
  
  // Check for upcoming features
  if (NOVA_KNOWLEDGE_BASE.upcoming.keywords.some(keyword => lowerInput.includes(keyword))) {
    return NOVA_KNOWLEDGE_BASE.upcoming.response;
  }
  
  // Check for file size limits
  if (NOVA_KNOWLEDGE_BASE.limits.keywords.some(keyword => lowerInput.includes(keyword))) {
    return NOVA_KNOWLEDGE_BASE.limits.response;
  }
  
  // Default response
  return "I'm here to help! You can ask me about:\n• How each PDF tool works\n• File security & privacy\n• Troubleshooting conversion issues\n• Account & membership questions\n\nWhat would you like to know?";
};

const NovaChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: NOVA_KNOWLEDGE_BASE.introduction.greeting,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate typing delay for natural feel
    setTimeout(() => {
      const botResponse = {
        id: messages.length + 2,
        type: 'bot',
        text: findResponse(userMessage.text),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 500 + Math.random() * 500);
  };

  const quickQuestions = [
    "How does PDF to Word work?",
    "Are my files secure?",
    "What's the file size limit?"
  ];

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
        style={{
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          boxShadow: '0 4px 20px rgba(37, 99, 235, 0.4)'
        }}
        aria-label="Toggle Nova Chat"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-48px)] rounded-2xl shadow-2xl overflow-hidden"
          style={{
            height: '500px',
            maxHeight: 'calc(100vh - 140px)',
            background: 'white',
            border: '1px solid #e5e7eb'
          }}
        >
          {/* Header */}
          <div
            className="p-4 flex items-center gap-3"
            style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
            }}
          >
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold text-lg">Nova</h3>
              <p className="text-white/80 text-xs">AI Support Assistant</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Messages Container */}
          <div
            className="flex-1 overflow-y-auto p-4 space-y-4"
            style={{ height: 'calc(100% - 140px)', background: '#f9fafb' }}
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                    message.type === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap" style={{ lineHeight: '1.5' }}>
                    {message.text}
                  </p>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions (show only at start) */}
          {messages.length === 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2" style={{ background: '#f9fafb' }}>
              {quickQuestions.map((question, index) => (
                <button
                  key={`quick-q-${index}-${question.slice(0, 20)}`}
                  onClick={() => {
                    setInputValue(question);
                    setTimeout(() => {
                      const form = document.getElementById('nova-chat-form');
                      if (form) form.requestSubmit();
                    }, 100);
                  }}
                  className="text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors border border-blue-200"
                >
                  {question}
                </button>
              ))}
            </div>
          )}

          {/* Input Area */}
          <form
            id="nova-chat-form"
            onSubmit={handleSendMessage}
            className="p-3 border-t border-gray-200 bg-white"
          >
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask Nova anything..."
                className="flex-1 px-4 py-2.5 rounded-full border border-gray-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm"
              />
              <button
                type="submit"
                disabled={!inputValue.trim()}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: inputValue.trim() 
                    ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' 
                    : '#e5e7eb'
                }}
              >
                <Send className="w-5 h-5 text-white" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default NovaChatbot;
